import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useParams } from "react-router-dom";
import { CompetitorTypes } from "~enums/competitorTypes";
import { useApi } from "~hooks/apiHooks/useApi";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import type { SearchFilterParams } from "~pages/FinalStandings/types/searchFilterParams";
import { transformPlacingResponse } from "~pages/FinalStandings/utilities/transform";
import type { PlacingEntityResponse } from "~types/placingEntity";
import type { PlacingPersonResponse } from "~types/placingPerson";
import type { SeasonEntityResponse } from "~types/seasonEntity";
import type { SeasonPersonResponse } from "~types/seasonPerson";

export const useFetchAllFinalPlacings = (params: SearchFilterParams) => {
  const enhancedRequest = useApi();
  const { organizationId, sport, seasonId } = useParams();
  const { competitorType } = useSportConfiguration();
  let entrantUrlPart = "entities";
  if (competitorType === CompetitorTypes.Person) {
    entrantUrlPart = "persons";
  }
  let seasonEntrantsUrl = `${sport}/o/${organizationId}/seasons/${seasonId}/${entrantUrlPart}`;

  if (!isEmpty(params)) {
    seasonEntrantsUrl += `?${new URLSearchParams(params)?.toString()}`;
  }

  let placingsUrl = `${sport}/o/${organizationId}/seasons/${seasonId}/${entrantUrlPart}/placings`;

  if (!isEmpty(params)) {
    placingsUrl += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["final-standings", sport, organizationId, seasonId, params],
    queryFn: async () => {
      const [seasonEntrants, placings] = await Promise.all([
        enhancedRequest<SeasonEntityResponse | SeasonPersonResponse>(seasonEntrantsUrl),
        enhancedRequest<PlacingEntityResponse | PlacingPersonResponse>(placingsUrl),
      ]);
      const data = transformPlacingResponse(seasonEntrants, placings, competitorType);
      return { data: data, nextLink: "" };
    },
  });
};
